// FormBuilder.tsx 

import React, { useEffect, useMemo, useState } from 'react';
import { Box, Group, Alert, Center, Loader } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';

import { BuilderField, FieldTypeCode, CustomFieldType, FIELD_TYPES, FieldTypeDef } from './types/BuilderField';
import { FormBuilderAdapter } from './types/BuilderAdapter';
import { useBuilder } from './hooks/useBuilder';
import { BuilderHeader } from './components/BuilderHeader';
import { FieldPalette } from './components/FieldPalette';
import { SectionList } from './components/SectionList';
import { FieldEditorModal, clearStoredCategories } from './components/FieldEditorModal';
import { PreviewDrawer } from './components/PreviewDrawer';

export interface FormBuilderProps {
  title?: string;
  subtitle?: string;
  adapter: FormBuilderAdapter;
  formTypes?: CustomFieldType[]; // Accepts API payload directly
  defaultCategories?: string[];
  readonly?: boolean;
  onChange?: (fields: BuilderField[]) => void;
  onSave?: () => void;
  onDone?: () => void;
}

export function FormBuilder({
  title = 'Form Builder',
  subtitle,
  adapter,
  formTypes,
  defaultCategories,
  readonly,
  onChange,
  onSave,
  onDone,
}: FormBuilderProps) {
  const {
    fields,
    sections,
    categories,
    loading,
    saving,
    error,
    setError,
    reload,
    saveField,
    deleteField,
    moveField,
  } = useBuilder({ adapter, defaultCategories, onChange });

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingField, setEditingField] = useState<BuilderField | null>(null);
  const [pendingType, setPendingType] = useState<FieldTypeCode | undefined>(undefined);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [customCategories, setCustomCategories] = useState<string[]>([]);
  const [resetToken, setResetToken] = useState(0);

  useEffect(() => {
    clearStoredCategories();
    setCustomCategories([]);
    setResetToken((t) => t + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Compute resolved types: Backend overrides > Frontend defaults
  const resolvedFieldTypes: FieldTypeDef[] = useMemo(() => {
    if (!formTypes || formTypes.length === 0) {
      return FIELD_TYPES;
    }
    
    return formTypes
      .filter((t) => t.isActive) // Ignore inactive types from the backend
      .map((t) => {
        // Map backend data kinds to frontend data kinds
        let mappedKind = t.dataKind;
        if (mappedKind === 'integer' || mappedKind === 'decimal') mappedKind = 'number';
        if (mappedKind === 'datetime') mappedKind = 'date';
        if (mappedKind === 'json') mappedKind = 'array';

        return {
          code: t.code,
          name: t.name,
          dataKind: mappedKind,
        };
      });
  }, [formTypes]);

  const availableCategories = useMemo(() => {
    const merged = Array.from(new Set([...(defaultCategories ?? []), ...categories, ...customCategories].filter(Boolean)));
    return merged;
  }, [categories, customCategories, defaultCategories]);

  const openNewFieldEditor = (type: FieldTypeCode) => {
    setEditingField(null);
    setPendingType(type);
    setEditorOpen(true);
  };

  const openEditFieldEditor = (field: BuilderField) => {
    setEditingField(field);
    setPendingType(undefined);
    setEditorOpen(true);
  };

  const handleSaveField = async (field: BuilderField) => {
    try {
      await saveField(field, editingField === null);
      setEditorOpen(false);
    } catch {
      // error state already surfaced via useBuilder
    }
  };

  const handleCreateCategory = (category: string) => {
    const trimmed = category.trim();
    if (!trimmed) return;
    setCustomCategories((prev) => Array.from(new Set([...prev, trimmed])));
  };

  const handleDone = () => {
    onDone?.();
    onSave?.();
    clearStoredCategories();
    setCustomCategories([]);
    setResetToken((t) => t + 1);
  };

  if (loading) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  return (
    <Box>
      <BuilderHeader
        title={title}
        subtitle={subtitle}
        saving={saving}
        readonly={readonly}
        onPreview={() => setPreviewOpen(true)}
        onDone={handleDone}
        onReload={reload}
      />

      {error && (
        <Alert
          color="red"
          icon={<IconAlertCircle size={16} />}
          mb="md"
          withCloseButton
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <Group align="flex-start" gap="md" wrap="nowrap">
        {!readonly && (
          <Box style={{ width: 220, flexShrink: 0 }}>
            <FieldPalette 
              types={resolvedFieldTypes} 
              onSelect={openNewFieldEditor} 
            />
          </Box>
        )}

        <Box style={{ flex: 1, minWidth: 0 }}>
          <SectionList
            sections={sections}
            readonly={readonly}
            onEdit={openEditFieldEditor}
            onDelete={deleteField}
            onMove={moveField}
          />
        </Box>
      </Group>

      {!readonly && (
        <FieldEditorModal
          opened={editorOpen}
          onClose={() => setEditorOpen(false)}
          onSave={handleSaveField}
          field={editingField}
          defaultType={pendingType}
          categories={availableCategories}
          existingFields={fields}
          onCreateCategory={handleCreateCategory}
          resetToken={resetToken}
          availableTypes={resolvedFieldTypes} 
        />
      )}

      <PreviewDrawer opened={previewOpen} onClose={() => setPreviewOpen(false)} fields={fields} />
    </Box>
  );
}

export default FormBuilder;